import { MovieCredit, MovieSummary, PagedResult, SeriesDetail, SeriesEpisode, SeriesSummary } from './api.service';
import { HomeCollectionSectionDto, HomeCollectionsDto } from './master-data-api.service';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      return value;
    }
  }

  return '';
}

function readIdList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => String(item)).filter((item) => item.length > 0);
}

export function unwrapCatalogItems<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) {
    return raw as T[];
  }

  const record = asRecord(raw);
  if (!record) {
    return [];
  }

  const items = record['items'] ?? record['Items'];
  return Array.isArray(items) ? (items as T[]) : [];
}

export function normalizeMovieSummary(raw: unknown): MovieSummary | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const id = readString(record, 'id', 'Id');
  if (!id) {
    return null;
  }

  return {
    id,
    titleEn: readString(record, 'titleEn', 'TitleEn'),
    titleVi: readString(record, 'titleVi', 'TitleVi'),
    genre: readString(record, 'genre', 'Genre'),
    releaseYear: Number(record['releaseYear'] ?? record['ReleaseYear'] ?? 0),
    durationMinutes: (record['durationMinutes'] ?? record['DurationMinutes']) as number | null | undefined,
    posterUrl: (record['posterUrl'] ?? record['PosterUrl']) as string | null | undefined,
    hasMedia: Boolean(record['hasMedia'] ?? record['HasMedia']),
    isVip: Boolean(record['isVip'] ?? record['IsVip']),
    isFeatured: Boolean(record['isFeatured'] ?? record['IsFeatured']),
    topCast: (record['topCast'] ?? record['TopCast']) as string | null | undefined,
    topDirectors: (record['topDirectors'] ?? record['TopDirectors']) as string | null | undefined
  };
}

export function normalizeSeriesSummary(raw: unknown): SeriesSummary | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const id = readString(record, 'id', 'Id');
  if (!id) {
    return null;
  }

  return {
    id,
    titleEn: readString(record, 'titleEn', 'TitleEn'),
    titleVi: readString(record, 'titleVi', 'TitleVi'),
    genre: readString(record, 'genre', 'Genre'),
    releaseYear: Number(record['releaseYear'] ?? record['ReleaseYear'] ?? 0),
    seasons: Number(record['seasons'] ?? record['Seasons'] ?? 0),
    episodes: Number(record['episodes'] ?? record['Episodes'] ?? 0),
    posterUrl: (record['posterUrl'] ?? record['PosterUrl']) as string | null | undefined,
    hasMedia: Boolean(record['hasMedia'] ?? record['HasMedia']),
    isVip: Boolean(record['isVip'] ?? record['IsVip']),
    isFeatured: Boolean(record['isFeatured'] ?? record['IsFeatured']),
    topCast: (record['topCast'] ?? record['TopCast']) as string | null | undefined,
    topDirectors: (record['topDirectors'] ?? record['TopDirectors']) as string | null | undefined
  };
}

function normalizeSeriesEpisode(raw: unknown): SeriesEpisode | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const id = readString(record, 'id', 'Id');
  if (!id) {
    return null;
  }

  const seasonYearRaw = record['seasonYear'] ?? record['SeasonYear'];
  const seasonYear =
    seasonYearRaw == null || seasonYearRaw === ''
      ? null
      : Number(seasonYearRaw);

  return {
    id,
    seasonNumber: Number(record['seasonNumber'] ?? record['SeasonNumber'] ?? 0),
    episodeNumber: Number(record['episodeNumber'] ?? record['EpisodeNumber'] ?? 0),
    title: readString(record, 'title', 'Title'),
    durationMinutes: (record['durationMinutes'] ?? record['DurationMinutes']) as number | null | undefined,
    hasMedia: Boolean(record['hasMedia'] ?? record['HasMedia']),
    thumbnailUrl: (record['thumbnailUrl'] ?? record['ThumbnailUrl']) as string | null | undefined,
    seasonYear: Number.isFinite(seasonYear) && (seasonYear as number) > 0 ? (seasonYear as number) : null
  };
}

export function normalizeSeriesDetail(raw: unknown): SeriesDetail | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const id = readString(record, 'id', 'Id');
  if (!id) {
    return null;
  }

  const episodesRaw = record['episodes'] ?? record['Episodes'];
  const episodes = Array.isArray(episodesRaw)
    ? episodesRaw
        .map((item) => normalizeSeriesEpisode(item))
        .filter((item): item is SeriesEpisode => item !== null)
    : [];

  return {
    id,
    titleEn: readString(record, 'titleEn', 'TitleEn'),
    titleVi: readString(record, 'titleVi', 'TitleVi'),
    genre: readString(record, 'genre', 'Genre'),
    releaseYear: Number(record['releaseYear'] ?? record['ReleaseYear'] ?? 0),
    description: (record['description'] ?? record['Description']) as string | null | undefined,
    descriptionEn: (record['descriptionEn'] ?? record['DescriptionEn']) as string | null | undefined,
    descriptionVi: (record['descriptionVi'] ?? record['DescriptionVi']) as string | null | undefined,
    directorEn: (record['directorEn'] ?? record['DirectorEn']) as string | null | undefined,
    directorVi: (record['directorVi'] ?? record['DirectorVi']) as string | null | undefined,
    castEn: (record['castEn'] ?? record['CastEn']) as string | null | undefined,
    castVi: (record['castVi'] ?? record['CastVi']) as string | null | undefined,
    directors: normalizeCredits(record['directors'] ?? record['Directors']),
    cast: normalizeCredits(record['cast'] ?? record['Cast']),
    posterUrl: (record['posterUrl'] ?? record['PosterUrl']) as string | null | undefined,
    isVip: Boolean(record['isVip'] ?? record['IsVip']),
    episodes
  };
}

function normalizeCredits(raw: unknown): MovieCredit[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  const items = raw
    .map((item) => {
      const record = asRecord(item);
      if (!record) {
        return null;
      }
      const name = readString(record, 'name', 'Name');
      if (!name) {
        return null;
      }
      const roleRaw = readString(record, 'role', 'Role').toLowerCase();
      const role = roleRaw === 'director' ? 'director' : 'actor';
      return {
        name,
        role,
        characterName: (record['characterName'] ?? record['CharacterName']) as string | null | undefined,
        profileImageUrl: (record['profileImageUrl'] ?? record['ProfileImageUrl']) as string | null | undefined,
        sortOrder: Number(record['sortOrder'] ?? record['SortOrder'] ?? 0),
        externalPersonId: (record['externalPersonId'] ?? record['ExternalPersonId']) as string | null | undefined,
        personId: (() => {
          const value = record['personId'] ?? record['PersonId'];
          if (value == null || value === '') {
            return null;
          }
          return String(value);
        })()
      } as MovieCredit;
    })
    .filter((item): item is MovieCredit => item !== null);

  return items.length > 0 ? items : null;
}

export function normalizeMovieList(raw: unknown): MovieSummary[] {
  return unwrapCatalogItems<unknown>(raw)
    .map((item) => normalizeMovieSummary(item))
    .filter((item): item is MovieSummary => item !== null);
}

export function normalizeSeriesList(raw: unknown): SeriesSummary[] {
  return unwrapCatalogItems<unknown>(raw)
    .map((item) => normalizeSeriesSummary(item))
    .filter((item): item is SeriesSummary => item !== null);
}

function readContentKind(record: Record<string, unknown>): 'movie' | 'series' {
  const raw = String(record['contentKind'] ?? record['ContentKind'] ?? 'movie').toLowerCase();
  return raw === 'series' ? 'series' : 'movie';
}

function normalizeHomeCollectionSection(raw: unknown): HomeCollectionSectionDto | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const id = readString(record, 'id', 'Id');
  if (!id) {
    return null;
  }

  const legacyMovieIds = readIdList(record['movieIds'] ?? record['MovieIds']);
  const legacySeriesIds = readIdList(record['seriesIds'] ?? record['SeriesIds']);
  const contentIds = readIdList(record['contentIds'] ?? record['ContentIds']);
  const contentKind = readContentKind(record);

  return {
    id,
    slug: readString(record, 'slug', 'Slug'),
    nameEn: readString(record, 'nameEn', 'NameEn'),
    nameVi: readString(record, 'nameVi', 'NameVi'),
    sortOrder: Number(record['sortOrder'] ?? record['SortOrder'] ?? 0),
    contentKind,
    contentIds:
      contentIds.length > 0
        ? contentIds
        : contentKind === 'series'
          ? legacySeriesIds
          : legacyMovieIds
  };
}

export function normalizeHomeCollections(raw: unknown): HomeCollectionSectionDto[] {
  const record = asRecord(raw);
  if (!record) {
    return [];
  }

  const collections = record['collections'] ?? record['Collections'];
  if (!Array.isArray(collections)) {
    return [];
  }

  return collections
    .map((item) => normalizeHomeCollectionSection(item))
    .filter((item): item is HomeCollectionSectionDto => item !== null);
}

export function normalizeCollectionCatalog(raw: unknown): HomeCollectionSectionDto | null {
  return normalizeHomeCollectionSection(raw);
}

export function catalogTotal(raw: unknown): number {
  if (Array.isArray(raw)) {
    return raw.length;
  }

  const record = asRecord(raw);
  if (!record) {
    return 0;
  }

  const total = record['total'] ?? record['Total'];
  return typeof total === 'number' ? total : 0;
}

export type { PagedResult, HomeCollectionsDto };
